import { useEffect, useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import {
  getShoppingList,
  type shoppingListProduct,
} from '../../../utils/shoppingListUtils'
import CollapsibleSection from '../../CollapsibleSection'
import ShoppingListEntry from './ShoppingListEntry'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import { getMessage } from '../../../utils/messageUtils'
import ShoppingListPopupContent from './ShoppingListPopupContent'

import './ShoppingList.scss'
import { getProductList, type product } from '../../../utils/productUtils'
import Spinner from '../../Spinner/Spinner'

interface ShoppingListProps {
  user: User
  isMobile: boolean
}

function ShoppingList({ user, isMobile }: ShoppingListProps) {
  const [loadData, setLoadData] = useState<boolean>(true)
  const [completeShoppingList, setCompleteShoppingList] = useState<
    shoppingListProduct[] | undefined
  >(undefined)
  const [shoppingList, setShoppingList] = useState<
    shoppingListProduct[] | undefined
  >([])
  const [shops, setShops] = useState<string[]>([])
  const [allProducts, setAllProducts] = useState<product[] | undefined>(
    undefined
  )

  // Popup
  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupExtraButtons, setPopupExtraButtons] = useState<
    ExtraButton[] | undefined
  >(undefined)
  const [popupOnOk, setPopupOnOk] = useState<(() => void) | undefined>(() => {})
  const [openShop, setOpenShop] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const fetchProducts = async (newList: shoppingListProduct[]) => {
      const data = await getProductList(user.id!)
      if (data.success) {
        const filteredProducts = data.products?.filter(
          product =>
            !newList.some(slProduct => slProduct.product === product.id)
        )
        setAllProducts(filteredProducts)
      }
    }

    const fetchList = async () => {
      setLoadData(false)
      const data = await getShoppingList(user.id!)
      if (data.success && data.results) {
        setCompleteShoppingList(data.results)
        const filteredList = data.results.filter(p => p.quantity > 0)
        setShoppingList(filteredList)
        const uniqueShops = Array.from(
          new Set(data.results.map(product => product.shop))
        )
        setShops(uniqueShops)
        fetchProducts(filteredList)
      }
    }
    if (loadData) {
      setIsLoading(true)
      fetchList().finally(() => setIsLoading(false))
    }
  }, [allProducts, loadData, shoppingList, user.id])

  const generateProductList = () => {
    let previousShop = ''
    const shops: { [key: string]: shoppingListProduct[] } = {}
    shoppingList?.forEach(product => {
      if (product.shop !== previousShop) {
        previousShop = product.shop
        shops[product.shop] = []
      }
      shops[product.shop].push(product)
    })

    let idx = -1
    return Object.entries(shops).map(([shop, products]) => {
      idx++
      const isFirstShop = idx === 0

      // Set the first shop as open initially if openShop is null
      if (openShop === null && isFirstShop) {
        setOpenShop(shop)
      }

      return (
        <CollapsibleSection
          key={shop}
          title={shop}
          isMobile={isMobile}
          isOpen={openShop === shop}
          onToggle={() => setOpenShop(openShop === shop ? null : shop)}
        >
          {products.map(product => (
            <ShoppingListEntry
              user={user}
              product={product}
              isMobile={isMobile}
              openPopup={openPopup}
              reLoadData={() => setLoadData(true)}
            />
          ))}
        </CollapsibleSection>
      )
    })
  }

  const openPopup = (productToEdit: shoppingListProduct | null) => {
    const title = productToEdit
      ? productToEdit.product_name!
      : getMessage('product_new')
    setPopupMessage(title)
    setPopupChildren(
      <ShoppingListPopupContent
        product={productToEdit}
        user={user}
        isMobile={isMobile}
        currentShops={shops}
        completeShoppingList={completeShoppingList}
        allProducts={allProducts}
        setPopupOnOk={setPopupOnOk}
        setPopupExtraButtons={setPopupExtraButtons}
        closePopup={() => setPopupOpen(false)}
        reLoadData={() => setLoadData(true)}
      />
    )
    setPopupOpen(true)
  }

  const handleNew = () => {
    openPopup(null)
  }

  return (
    <div className="shopping-list-container">
      <div
        className={`shopping-list-header ${isMobile ? 'shopping-list-header-mobile' : ''}`}
      >
        <button className="shopping-list-header-button" onClick={handleNew}>
          {getMessage('add')}
        </button>
      </div>
      <div className="shopping-list-content">{generateProductList()}</div>
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        isMobile={isMobile}
        extraButtons={popupExtraButtons}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default ShoppingList
